import { useSelector } from 'react-redux';
import Media from 'react-media';
import loadable from '@loadable/component';

import { useMount, useTheme } from '@simplywallst/ui-core';
import type { Theme } from '@simplywallst/ui-core';

import { getUi } from '@/redux/ui/selectors';

import { useSnackbar } from '@/features/snackbar/context';

import SwitcherContainer from '@/containers/SwitcherContainer';

import Onboarding from '@/components/Onboarding';
import { BrazeProvider } from '@/components/Braze';
import { LayoutProvider } from '@/components/Layout/context/LayoutContext';

import { useUser } from '@/hooks/useUser';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { useAppInit } from './hooks/useAppInit';

import { Body } from './styled';
import { MainContainer } from './components/MainContainer';
import { AuthChecker } from '@/components/AuthChecker';
import { SimplyWallStSchema } from '@/components/DocumentSchema';
import { useTranslation } from '@/hooks/useTranslation';

const Snackbar = loadable(() => import('@/features/snackbar'), {
  resolveComponent: (c) => c.Snackbar,
  ssr: false,
});

const LoadingOverlay = loadable(() => import('@/components/LoadingOverlay'), {
  ssr: false,
});

const MobileFooterMenuContainer = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/components/MobileFooterMenu/containers/MobileFooterMenuContainer'
    )
);
const TrialEndedPlansModal = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/components/OurPlans/components/TrialEndedPlansModal/TrialEndedPlansModal'
    )
);
const CheckoutReturnModal = loadable(
  () => import('@/components/OurPlans/components/CheckoutReturnModal')
);

const AppUpdate = loadable(() => import('@/components/AppUpdate'), {
  ssr: false,
  resolveComponent: (component) => component.AppUpdate,
});

const AppUrlListener = loadable(() => import('@/components/AppUrlListener'), {
  resolveComponent: (component) => component.AppUrlListener,
});

export interface WithThemeProps {
  theme: Theme;
}

export interface Props {
  layout: Record<string, any>;
}

const Content = ({ layout }: Props) => {
  const theme = useTheme();
  const ui = useSelector(getUi);
  const snackbar = useSnackbar();
  const { data: user } = useUser();
  const { isMobile, isTablet } = useUserAgent();

  const isLoading = ui.loading;
  const hasSnackbar = snackbar.state.keys.length > 0;
  const t = useTranslation();

  useMount(() => {
    // TODO: remove this, used for internal testing in PLAT-1216.
    console.info(
      t('app.topbar.nav.mobile.account.appVersion', {
        version: 'v2.9.34',
        build: '2911',
      })
    );
  });

  useAppInit();

  return (
    <MainContainer>
      <BrazeProvider>
        <LayoutProvider layout={layout}>
          <Body
            justifyContent="center"
            hasMobileFooterMenu={layout && layout.mobileFooterMenu !== false}
          >
            <AuthChecker />
            <SimplyWallStSchema />
            <SwitcherContainer />
          </Body>
          {layout && layout.mobileFooterMenu !== false && (
            <Media
              query={{ maxWidth: theme.mdMaxRaw }}
              defaultMatches={isMobile || isTablet}
              render={() => <MobileFooterMenuContainer />}
            />
          )}
          {layout && (
            <>
              {hasSnackbar && <Snackbar />}
              {isLoading && <LoadingOverlay />}
              <TrialEndedPlansModal />
              {user.loggedIn && <CheckoutReturnModal />}
              <Onboarding />
            </>
          )}
          {RUNTIME_ENV === 'native' && user.loggedIn && <AppUpdate />}
          {RUNTIME_ENV === 'native' && <AppUrlListener />}
        </LayoutProvider>
      </BrazeProvider>
    </MainContainer>
  );
};

export default Content;
